import { type ReactElement, useEffect, useMemo, useState } from 'react';
import {
  defaultTagColor,
  normalizeTagsAndColors,
} from '@/features/shared/components/entityDetails/EditableTagsSection.utils';
import {
  editDialogInputBackgroundColor,
  EntityEditDialog,
} from '@/features/shared/components/entityDetails/EntityEditDialog';
import { EntityTagsField } from '@/features/shared/components/tags/EntityTagsField';
import { getFormLabel } from '@/shared/copy/forms';
import { useTagOptions } from '@/shared/hooks';
import { TextField } from '@/ui/primitives/TextField';
import { type TestFormErrors, type TestFormHandlers, type TestFormValues } from './types';
import type { TagColor } from '@/shared/data/tags';

type EditTestDialogProps = {
  open: boolean;
  saving?: boolean;
  values: TestFormValues;
  projectId?: string | null;
  tags?: string[];
  errors?: TestFormErrors;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: (input: { tags: string[]; colors: TagColor[] }) => void;
  handlers: TestFormHandlers;
};

export const EditTestDialog = ({
  open,
  saving = false,
  values,
  projectId = null,
  tags = [],
  errors,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: EditTestDialogProps): ReactElement => {
  const Field = TextField;
  const { tagOptions } = useTagOptions({ projectId, enabled: open });
  const [draftTags, setDraftTags] = useState<string[]>(tags);
  const [draftTagColors, setDraftTagColors] = useState<TagColor[]>(
    tags.map((_tag, index) => tagOptions[index]?.color ?? defaultTagColor),
  );
  const initialTagColors = useMemo(
    () =>
      tags.map((tag) => {
        const matchingOption = tagOptions.find((option) => option.value === tag);
        return matchingOption?.color ?? defaultTagColor;
      }),
    [tagOptions, tags],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraftTags(tags);
    setDraftTagColors(initialTagColors);
  }, [initialTagColors, open, tags]);

  const normalizedDraftTags = normalizeTagsAndColors(draftTags, draftTagColors);

  return (
    <EntityEditDialog
      open={open}
      entityLabel="Test"
      entityName={values.name}
      saving={saving}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={() => {
        onSave(normalizedDraftTags);
      }}
    >
      <Field
        label={getFormLabel('name')}
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        fieldStyle="settings"
        inputBackgroundColor={editDialogInputBackgroundColor}
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      <EntityTagsField
        tags={draftTags}
        colors={draftTagColors}
        helperText="Tags will be applied to all associated test runs."
        tagOptions={tagOptions}
        onTagsChange={setDraftTags}
        onColorsChange={setDraftTagColors}
      />
    </EntityEditDialog>
  );
};
